import { useState } from "react";
import { Upload } from "lucide-react";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { EmptyState } from "#/components/common/EmptyState";
import { DataPagination } from "#/components/data-table/DataPagination";
import { useDebouncedValue } from "#/features/inventory/hooks";
import { useMyDocuments } from "#/features/documents/hooks";
import { CURRENT_YEAR, hasCustomFilters, MyDocumentFiltersBar } from "#/features/documents/components/DocumentFilters";
import { DocumentViewer } from "#/features/documents/components/DocumentViewer";
import { DocumentUploadForm } from "#/features/documents/components/DocumentUploadForm";
import type { MyDocumentFilters } from "#/features/documents/types";

// ── Page ──────────────────────────────────────────────────────────────────────

export default function MyDocumentsPage() {
  const [page, setPage] = useState(0);
  // Defaults to the current year — tick "All Years" in the filter bar to see everything.
  const [filters, setFilters] = useState<MyDocumentFilters>({ year: CURRENT_YEAR });
  const [showUpload, setShowUpload] = useState(false);

  // Debounce the free-text search so the query only fires once typing pauses.
  const debouncedSearch = useDebouncedValue(filters.search, 400);
  const effectiveFilters: MyDocumentFilters = { ...filters, search: debouncedSearch };

  const query = useMyDocuments(page, 20, effectiveFilters);

  function handleFilterChange(f: MyDocumentFilters) {
    setFilters(f);
    setPage(0);
  }

  if (query.isLoading) return <PageLoader label="Loading your documents…" />;

  if (query.isError) {
    return (
      <ErrorState
        title="Failed to load documents"
        message={
          query.error instanceof Error ? query.error.message : "Could not load documents."
        }
      />
    );
  }

  const data = query.data!;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Documents</h1>
        </div>
        <button
          type="button"
          onClick={() => setShowUpload((v) => !v)}
          className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
        >
          <Upload className="h-4 w-4" />
          {showUpload ? "Cancel" : "Upload Document"}
        </button>
      </div>

      {/* Upload panel */}
      {showUpload && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5">
          <h2 className="mb-3 font-bold text-slate-800">Upload a document</h2>
          <DocumentUploadForm
            onUploaded={() => {
              setShowUpload(false);
              void query.refetch();
            }}
          />
        </div>
      )}

      {/* Filters */}
      <MyDocumentFiltersBar filters={filters} onChange={handleFilterChange} />

      {/* Summary line */}
      {data.totalElements > 0 && (
        <p className="text-sm text-slate-500">
          {data.totalElements.toLocaleString()} document{data.totalElements !== 1 ? "s" : ""} found
        </p>
      )}

      {/* List */}
      {data.content.length === 0 ? (
        <EmptyState
          title="No documents found"
          description={
            hasCustomFilters(filters)
              ? "Nothing matches your filters."
              : `Nothing from ${CURRENT_YEAR} yet — tick "All Years" to see older documents, or use the button above to upload.`
          }
        />
      ) : (
        <>
          <DocumentViewer documents={data.content} />

          <DataPagination
            page={data.number}
            totalPages={data.totalPages}
            totalElements={data.totalElements}
            size={data.size}
            onPageChange={setPage}
            onSizeChange={() => setPage(0)}
          />
        </>
      )}
    </div>
  );
}
